import { UmCountries } from '@shared/models/umCountries.model';
import { GeneralAppVariables } from '@shared/models/general-app-variables.model';
import { ResponseStatus } from '@shared/models/base/response-status.model';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { effect, inject } from '@angular/core';
import { CountryService } from '@shared/services/country.service';
import { GeneralAppVariablesService } from '@shared/services/general-app-variables.service';
import { tapResponse } from '@ngrx/operators';
import { IResponse } from '@shared/models/base/http-response.model';
import { SessionStorageService } from '@shared/services/session-storage.service';
import { Logger } from '@shared/services/logger';
import { MarkDefinition } from '@shared/components/organisms/search-bar/models/mark-definition.model';
import { MarkDefinitionsService } from '@shared/services/mark-definitions.service';
import { withReset } from '@shared/stores/features/with-reset';

export interface CommonState {
  countries: UmCountries[];
  countriesDataStatus: ResponseStatus;
  markDefinitions: MarkDefinition[];
  markDefinitionsDataStatus: ResponseStatus;
  variables: GeneralAppVariables;
  variablesDataStatus: ResponseStatus;
}

const COUNTRIES_STORAGE_KEY = 'countries';
const MARK_DEFINITIONS_STORAGE_KEY = 'markDefinitions';
const VARIABLES_STORAGE_KEY = 'variables';

export const CommonStore = signalStore(
  { providedIn: 'root' },
  withDevtools('common'),
  withState(_initStateFn),
  withReset('common'),
  withMethods(
    (
      store,
      countryService = inject(CountryService),
      markDefinitionsService = inject(MarkDefinitionsService),
      generalAppVariablesService = inject(GeneralAppVariablesService),
      sessionService = inject(SessionStorageService),
      logger = inject(Logger),
    ) => ({
      getCountries: rxMethod<void>(
        pipe(
          filter(() => store.countries.length === 0 && store.countriesDataStatus() === ResponseStatus.IDLE),
          tap(() => updateState(store, 'countries - loading', { countriesDataStatus: ResponseStatus.LOADING })),
          switchMap(() => {
            return countryService.getCountries().pipe(
              tapResponse({
                next: (umCountries: IResponse<UmCountries[]>) => {
                  updateState(store, 'countries - loaded', {
                    countries: umCountries.data,
                    countriesDataStatus: ResponseStatus.LOADED,
                  });

                  sessionService.setItem(COUNTRIES_STORAGE_KEY, umCountries.data);
                },
                error: (err) => {
                  logger.error('countries - error', err);
                  updateState(store, 'countries - error', { countriesDataStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      // getVariables: rxMethod<void>(
      //   pipe(
      //     filter(() => !store.variables().idleTimeInMinutes && store.variablesDataStatus() === ResponseStatus.IDLE),
      //     tap(() => updateState(store, 'variables - loading', { variablesDataStatus: ResponseStatus.LOADING })),
      //     switchMap(() => {
      //       return generalAppVariablesService.getVariables().pipe(
      //         tapResponse({
      //           next: (variables: IResponse<GeneralAppVariables>) => {
      //             updateState(store, 'variables - loaded', {
      //               variables: variables.data,
      //               variablesDataStatus: ResponseStatus.LOADED,
      //             });
      //             sessionService.setItem(VARIABLES_STORAGE_KEY, variables.data);
      //           },
      //           error: (err) => {
      //             logger.error('variables - error', err);
      //             updateState(store, 'variables - error', { variablesDataStatus: ResponseStatus.ERROR });
      //           },
      //         }),
      //       );
      //     }),
      //   ),
      // ),
      getMarkDefinitions: rxMethod<void>(
        pipe(
          filter(() => store.markDefinitions.length === 0 && store.markDefinitionsDataStatus() === ResponseStatus.IDLE),
          tap(() => patchState(store, { markDefinitionsDataStatus: ResponseStatus.LOADING })),
          switchMap(() => {
            return markDefinitionsService.getMarkDefinitions().pipe(
              tapResponse({
                next: (markDefinitions: IResponse<MarkDefinition[]>) => {
                  patchState(store, {
                    markDefinitions: markDefinitions.data,
                    markDefinitionsDataStatus: ResponseStatus.LOADED,
                  });

                  sessionService.setItem(MARK_DEFINITIONS_STORAGE_KEY, markDefinitions.data);
                },
                error: (err) => {
                  logger.error('unable to load mark definitions', err);
                  patchState(store, { markDefinitionsDataStatus: ResponseStatus.ERROR });
                },
              }),
            );
          }),
        ),
      ),
      setVariables(): void {
        const variables = sessionService.getItem<GeneralAppVariables>(VARIABLES_STORAGE_KEY);
        if (variables) {
          patchState(store, { variables: variables, variablesDataStatus: ResponseStatus.LOADED });
        }
      },
    }),
  ),
  withHooks({
    onInit: (store) => {
      const initEffectRef = effect(() => {
        if (store.countriesDataStatus() === ResponseStatus.IDLE) {
          store.getCountries();
        } else {
          initEffectRef.destroy();
        }
      });

      // Fetched eagerly, same as countries above — consumers like the
      // rich-text-editor's Data Mark toolbar picker need markDefinitions
      // synchronously available by the time Quill builds its toolbar (Quill
      // never rebuilds the toolbar DOM after construction), so waiting until
      // first use is too late.
      const markDefinitionsEffectRef = effect(() => {
        if (store.markDefinitionsDataStatus() === ResponseStatus.IDLE) {
          store.getMarkDefinitions();
        } else {
          markDefinitionsEffectRef.destroy();
        }
      });
    }
  }),
);

function _initStateFn(): CommonState {
  const sessionService = inject(SessionStorageService);
  const storedCountries = sessionService.getItem<UmCountries[]>(COUNTRIES_STORAGE_KEY);
  const hasStoredCountries = !!storedCountries?.length;
  const storedMarkDefinitions = sessionService.getItem<MarkDefinition[]>(MARK_DEFINITIONS_STORAGE_KEY);
  const hasStoredMarkDefinitions = !!storedMarkDefinitions?.length;
  const storedVariables = sessionService.getItem<GeneralAppVariables>(VARIABLES_STORAGE_KEY);
  const hasStoredVariables = !!storedVariables;

  return {
    countries: hasStoredCountries ? storedCountries : [],
    countriesDataStatus: hasStoredCountries ? ResponseStatus.LOADED : ResponseStatus.IDLE,
    markDefinitions: hasStoredMarkDefinitions ? storedMarkDefinitions : [],
    markDefinitionsDataStatus: hasStoredMarkDefinitions ? ResponseStatus.LOADED : ResponseStatus.IDLE,
    variables: hasStoredVariables ? storedVariables : new GeneralAppVariables(),
    variablesDataStatus: hasStoredVariables ? ResponseStatus.LOADED : ResponseStatus.IDLE,
  } as CommonState;
}
